import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrders } from '../hooks/useOrders'
import { StatusBadge } from '../components/common/Badge'
import { exportarPlanilha } from '../utils/excel'
import { gerarPDF } from '../utils/pdf'
import { getConfig, getBlingHistorico, getBlingContatos, pedidoTemEspelhoBling } from '../utils/storage'
import { useAuth } from '../contexts/AuthContext'
import { AlertTriangle } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  Search, Plus, Sheet, FileDown, Trash2, ChevronRight, Package, ExternalLink, Filter, ChevronDown, Pencil
} from 'lucide-react'
import { usePedidosFiltros } from '../hooks/usePedidosFiltros'
import { FiltroDrawer } from '../components/FiltroDrawer'
import { EditarClienteModal } from '../components/EditarClienteModal'
import { savePedido } from '../utils/storage'
import { KANBAN_COLUNAS } from '../data/kanban'
import { formatCurrency } from '../utils/fmt'

const STATUS_COLORS = {
  A_FABRICAR: '#94A3B8', FABRICANDO: '#FBBF24', FABRICADO: '#60A5FA',
  EXPEDICAO: '#4ADE80', ROTA: '#FB923C', POS_VENDA: '#C084FC',
}

function normalizarPedidoBling(p, contatosMap = new Map()) {
  const total      = Number(p.totalVenda ?? p.total ?? p.totais?.total ?? 0)
  const prazo      = p.dataPrevista && p.dataPrevista !== '0000-00-00' ? p.dataPrevista : null
  const cont       = contatosMap.get(String(p.contato?.id || '')) || {}
  const end        = cont.endereco?.geral || {}
  const enderecoFull = [end.endereco, end.numero, end.complemento, end.bairro, end.municipio]
    .filter(Boolean).join(', ')
  return {
    id:         `bling-${p.id}`,
    numero:     p.numeroLoja || `BL-${p.numero || p.id}`,
    dataVenda:  p.data  || null,
    prazo,
    valorTotal: total,
    status:     'A_FABRICAR',
    origem:     'bling',
    cliente: {
      nome:     p.contato?.nome || cont.nome || '—',
      contato:  p.contato?.celular || p.contato?.telefone || cont.celular || cont.telefone || '',
      email:    cont.email || '',
      endereco: enderecoFull,
      bairro:   end.bairro    || '',
      cidade:   end.municipio || '',
      cep:      end.cep       || '',
      uf:       end.uf        || '',
    },
    moveis: [],
    itens:  [],
  }
}


function formatDate(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return d }
}

export function Orders() {
  const navigate             = useNavigate()
  const { profile }          = useAuth()
  const ehAdminOuGerente     = profile?.role === 'admin' || profile?.role === 'gerente'
  const { pedidos, remover, moverStatus, refresh } = useOrders()
  const [showFiltro, setShowFiltro]           = useState(false)
  // Guarda o pedido inteiro em vez do id só, pra o modal poder detectar
  // se tem espelho no Bling e mostrar aviso apropriado.
  const [confirmDelete, setConfirmDelete]     = useState(null)
  const [statusDropdown, setStatusDropdown]   = useState(null)
  const [statusOverrides, setStatusOverrides] = useState({})
  // Pedido em edição inline do cliente (lápis na linha). null = fechado.
  const [editandoCliente, setEditandoCliente] = useState(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (!statusDropdown) return
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStatusDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [statusDropdown])
  const sheetsUrl = getConfig().googleSheetsViewUrl

  const { filtros, totalAtivos, limpar, toggle, set, filtrar } = usePedidosFiltros()

  // Combina pedidos locais com histórico Bling
  const todos = useMemo(() => {
    const locais      = pedidos
    const numerosLocais = new Set(locais.map(p => p.numero))
    const contatosMap = new Map(getBlingContatos().map(c => [String(c.id), c]))
    const doBling     = getBlingHistorico()
      .map(p => normalizarPedidoBling(p, contatosMap))
      .filter(p => !numerosLocais.has(p.numero))
    return [...locais, ...doBling].sort((a, b) => {
      const da = a.dataVenda || (a.criadoEm || '').split('T')[0] || ''
      const db = b.dataVenda || (b.criadoEm || '').split('T')[0] || ''
      return db.localeCompare(da) // mais recente primeiro
    })
  }, [pedidos])

  const filtrados = useMemo(() => filtrar(todos), [todos, filtrar])

  function handleDelete(id) {
    remover(id)
    setConfirmDelete(null)
  }
  function abrirConfirmDelete(p) {
    setConfirmDelete({
      id:         p.id,
      numero:     p.numero,
      cliente:    p.cliente?.nome || '—',
      origem:     p.origem,  // 'bling' pra pedidos-fantasma Bling
      temBling:   p.origem !== 'bling' && pedidoTemEspelhoBling(p.numero),
    })
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Pedidos</h1>
          <p className="text-sm text-slate-400 mt-0.5">{todos.length} pedido{todos.length !== 1 ? 's' : ''} no total</p>
        </div>
        <div className="flex gap-2">
          {sheetsUrl && (
            <a href={sheetsUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm">
              <ExternalLink size={15} className="text-green-600" />
              Planilha mãe
            </a>
          )}
          <button onClick={() => exportarPlanilha(pedidos)} className="btn-secondary text-sm">
            <Sheet size={15} />
            Exportar Excel
          </button>
          <button onClick={() => navigate('/pedido/novo')} className="btn-primary text-sm">
            <Plus size={15} />
            Novo pedido
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            value={filtros.busca}
            onChange={e => set('busca', e.target.value)}
            placeholder="Buscar por número ou cliente..."
            className="input pl-9 text-sm"
          />
        </div>
        <button
          onClick={() => setShowFiltro(o => !o)}
          className={`btn-secondary text-sm ${showFiltro ? 'ring-2 ring-[#F5C200]' : ''}`}
        >
          <Filter size={14} />
          Filtrar
          {totalAtivos > 0 && (
            <span className="ml-1 bg-[#F5C200] text-[#111111] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {totalAtivos}
            </span>
          )}
        </button>
      </div>

      <FiltroDrawer
        open={showFiltro}
        onClose={() => setShowFiltro(false)}
        filtros={filtros}
        toggle={toggle}
        set={set}
        limpar={limpar}
        totalAtivos={totalAtivos}
        pedidos={todos}
      />

      {filtrados.length === 0 ? (
        <div className="card p-16 text-center">
          <Package className="mx-auto text-slate-200 mb-3" size={40} />
          <p className="text-slate-400 text-sm">
            {pedidos.length === 0
              ? 'Nenhum pedido cadastrado ainda'
              : 'Nenhum pedido encontrado para os filtros aplicados'
            }
          </p>
          {pedidos.length === 0 && (
            <button onClick={() => navigate('/pedido/novo')} className="btn-primary mx-auto mt-4 text-sm">
              <Plus size={15} />
              Criar primeiro pedido
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nº</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Prazo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Itens</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtrados.map(p => {
                const isBling = p.origem === 'bling'
                return (
                  <tr
                    key={p.id}
                    className={`transition-colors group cursor-pointer ${isBling ? 'bg-blue-50/30 hover:bg-blue-50/60' : 'hover:bg-slate-50/80'}`}
                    onClick={() => navigate(`/pedidos/${p.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-slate-500">{p.numero}</span>
                        {isBling && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">Bling</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{p.cliente?.nome}</p>
                      <p className="text-xs text-slate-400">{p.cliente?.contato}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(p.prazo)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {(() => {
                        const n = isBling ? (p.itens?.length || 0) : (p.moveis?.length || 0)
                        return n > 0
                          ? `${n} item${n !== 1 ? 's' : ''}`
                          : <span className="text-slate-300">—</span>
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Total = valor dos móveis (com desconto) + frete */}
                      <p className="font-semibold text-slate-800">
                        {formatCurrency((Number(p.valorTotal) || 0) + (Number(p.frete) || 0))}
                      </p>
                      {Number(p.frete) > 0 && (
                        <p className="text-[10px] text-slate-400 leading-tight">
                          {formatCurrency(p.valorTotal)} + {formatCurrency(p.frete)} frete
                        </p>
                      )}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={e => { e.stopPropagation(); setStatusDropdown(statusDropdown === p.id ? null : p.id) }}
                    >
                      <div className="relative inline-block" ref={statusDropdown === p.id ? dropdownRef : null}>
                        <div className="flex items-center gap-1 cursor-pointer group/status">
                          <StatusBadge status={statusOverrides[p.id] ?? p.status} />
                          <ChevronDown size={10} className="text-slate-300 group-hover/status:text-slate-500 transition-colors shrink-0" />
                        </div>
                        {statusDropdown === p.id && (
                          <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-30 w-44 overflow-hidden">
                            {KANBAN_COLUNAS.map(col => (
                              <button
                                key={col.id}
                                onClick={e => {
                                  e.stopPropagation()
                                  moverStatus(p.id, col.id)
                                  setStatusOverrides(prev => ({ ...prev, [p.id]: col.id }))
                                  setStatusDropdown(null)
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-slate-50 transition-colors"
                              >
                                <span
                                  className="w-2 h-2 rounded-full shrink-0"
                                  style={{ backgroundColor: STATUS_COLORS[col.id] }}
                                />
                                {col.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {/* Ações: lápis (editar cliente) sempre visível pra
                          funcionar no mobile; PDF/lixeira só no hover. */}
                      <div className="flex items-center gap-1 justify-end">
                        {!isBling && (
                          <>
                            {/* Lápis SEMPRE visível — feature destacada
                                pra vendedora achar no mobile sem hover */}
                            <button
                              onClick={e => { e.stopPropagation(); setEditandoCliente(p) }}
                              title="Editar cliente (nome, CPF/CNPJ, contato, e-mail)"
                              className="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); gerarPDF(p) }}
                              title="Gerar PDF"
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FileDown size={14} />
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); abrirConfirmDelete(p) }}
                              title="Excluir"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={14} />
                            </button>
                            <ChevronRight size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Editar cliente inline (lápis) — só pedidos locais */}
      {editandoCliente && (
        <EditarClienteModal
          pedido={editandoCliente}
          onClose={() => setEditandoCliente(null)}
          onSalvar={(atualizado) => {
            savePedido(atualizado)
            refresh()
            setEditandoCliente(null)
          }}
        />
      )}

      {/* Confirm delete — modo dinâmico:
          - Se pedido tem espelho Bling ativo E user é vendedora: bloqueia
          - Se tem espelho E user é admin/gerente: aviso amarelo + botão perigoso
          - Se não tem espelho (pedido Bling puro ou local sem espelho): fluxo comum */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            {confirmDelete.temBling ? (
              <>
                <div className="flex items-start gap-3 mb-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Este pedido também existe no Bling</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Pedido {confirmDelete.numero} · {confirmDelete.cliente}</p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 mb-4 leading-relaxed">
                  Deletar aqui <strong>NÃO cancela no Bling</strong> — só apaga os dados locais
                  (valor pendente, observações, split de pagamento). O pedido continua aparecendo
                  como pedido Bling puro, sem esses campos.
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    Se quer cancelar o pedido de verdade, <strong>cancele no Bling primeiro</strong>.
                  </div>
                </div>
                {ehAdminOuGerente ? (
                  <div className="flex gap-3">
                    <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1 justify-center">Voltar</button>
                    <button onClick={() => handleDelete(confirmDelete.id)} className="btn-danger flex-1 justify-center">Deletar mesmo assim</button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => setConfirmDelete(null)} className="btn-primary flex-1 justify-center">Voltar</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="font-semibold text-slate-800 mb-2">Excluir pedido?</h3>
                <p className="text-sm text-slate-500 mb-5">
                  {confirmDelete.numero} · {confirmDelete.cliente}
                  <br/>
                  <span className="text-xs text-slate-400">Vai pra Lixeira — dá pra restaurar depois.</span>
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1 justify-center">Cancelar</button>
                  <button onClick={() => handleDelete(confirmDelete.id)} className="btn-danger flex-1 justify-center">Excluir</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
